import type { ModuleContext, ModuleSetup } from "@crialume/sdk";
import { ModuleContextProvider } from "@crialume/sdk/react";
import { Suspense, useEffect, useState, type ReactNode } from "react";
import { useParams, useRoutes, type RouteObject } from "react-router";
import { checkModuleAccess } from "../registry/access";
import { ModuleErrorBoundary } from "./module-error-boundary";
import { useAuthSession, useKernel } from "./kernel-context";

/**
 * Telas de estado do outlet. O shell injeta as versões do design system —
 * o core não renderiza UI própria além de um fallback mínimo.
 */
export interface ModuleOutletSlots {
  loading: ReactNode;
  notFound: ReactNode;
  forbidden: ReactNode;
  error: (error: Error, retry: () => void) => ReactNode;
}

type LoaderState =
  | { phase: "loading"; moduleId: string }
  | { phase: "ready"; moduleId: string; setup: ModuleSetup; context: ModuleContext }
  | { phase: "error"; moduleId: string; error: Error };

const CHUNK_RELOAD_FLAG = "crialume:chunk-reload";
const STALE_CHUNK =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

/**
 * Renderiza o módulo de `/app/:moduleId/*`:
 * 1. checa acesso ANTES de baixar o chunk;
 * 2. carrega (deduplicado pelo registry);
 * 3. provê o ModuleContext escopado e isola erros de render.
 */
export function ModuleOutlet({ slots }: { slots: ModuleOutletSlots }) {
  const { moduleId = "" } = useParams<{ moduleId: string }>();
  const kernel = useKernel();
  const session = useAuthSession();
  const [state, setState] = useState<LoaderState>({ phase: "loading", moduleId });
  const [attempt, setAttempt] = useState(0);

  const manifest = kernel.registry.get(moduleId);
  const access = manifest ? checkModuleAccess(manifest, { session, config: kernel.config }) : null;
  const allowed = access?.allowed === true;

  // Troca de módulo cai em "loading" no render (state.moduleId !== moduleId);
  // o retry marca "loading" no próprio clique. O efeito só sincroniza a carga.
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;

    kernel.registry
      .load(moduleId)
      .then((setup) => {
        if (cancelled) return;
        const context = kernel.registry.getContext(moduleId)!;
        setState({ phase: "ready", moduleId, setup, context });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        const error = cause instanceof Error ? cause : new Error(String(cause));
        // Deploy novo invalidou os chunks antigos: recarrega uma vez.
        if (STALE_CHUNK.test(error.message) && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
          window.location.reload();
          return;
        }
        setState({ phase: "error", moduleId, error });
      });

    return () => {
      cancelled = true;
    };
  }, [kernel, moduleId, allowed, attempt]);

  if (!manifest) return <>{slots.notFound}</>;
  if (!allowed) return <>{slots.forbidden}</>;
  if (state.moduleId !== moduleId || state.phase === "loading") return <>{slots.loading}</>;
  if (state.phase === "error") {
    const retry = () => {
      setState({ phase: "loading", moduleId });
      setAttempt((n) => n + 1);
    };
    return <>{slots.error(state.error, retry)}</>;
  }

  return (
    <ModuleContextProvider value={state.context}>
      <ModuleErrorBoundary
        moduleId={moduleId}
        fallback={slots.error}
        onError={(error) => kernel.telemetry.trackError(error, { moduleId, phase: "render" })}
      >
        <Suspense fallback={slots.loading}>
          {/* key: troca de módulo remonta a árvore de rotas inteira. */}
          <ModuleRoutes key={moduleId} routes={state.setup.routes} />
        </Suspense>
      </ModuleErrorBoundary>
    </ModuleContextProvider>
  );
}

function ModuleRoutes({ routes }: { routes: RouteObject[] }) {
  return useRoutes(routes);
}
